import React from 'react';
import { isfi, onShift } from '../../engine/data';
import type { Worker } from '../../engine/data';
import { Banner, FBar, Grid, Panel, toneText } from '../../ui';
import type { Tone } from '../../ui';

const LabelBar: React.FC<{ label: string; value: React.ReactNode; bar: number; tone: Tone }> = ({
  label,
  value,
  bar,
  tone,
}) => (
  <div className='text-xs'>
    {label} <span className='font-semibold'>{value}</span>
    <FBar value={bar} tone={tone} className='mt-0.5' />
  </div>
);

export const SourceIsfiPanels: React.FC<{ w: Worker }> = ({ w }) => {
  const fi = isfi(w);
  const hrElev = w.hr_work_b > 0 ? Math.round(((w.hr_work - w.hr_work_b) / w.hr_work_b) * 100) : 0;
  const assess = Math.round((w.assessScore / 20) * 100);
  const fiTone: Tone = fi >= 75 ? 'bad' : fi >= 50 ? 'warn' : 'good';

  return (
    <Grid cols={2}>
      <Panel title='Fatigue Source Split' sub='In-work vs out-of-work contribution'>
        <div className='flex flex-wrap items-center gap-6 py-2'>
          <div>
            <div className='flex h-4 w-40 overflow-hidden rounded-full bg-muted'>
              <div className='h-4 bg-info' style={{ width: `${w.inWorkFed}%` }} />
              <div className='h-4 bg-violet' style={{ width: `${w.outWorkFed}%` }} />
            </div>
            <div className='mt-2 flex gap-3 text-xs'>
              <span className='text-info'>■ In-work {w.inWorkFed}%</span>
              <span className='text-violet'>■ Out-of-work {w.outWorkFed}%</span>
            </div>
          </div>
          <div className='min-w-[180px] flex-1'>
            {w.inWorkFed >= 60 ? (
              <Banner tone='bad' className='text-xs'>
                Primary source: shift/driving demands. Review rota and rest breaks.
              </Banner>
            ) : (
              <Banner tone='info' className='text-xs'>
                Primary source: out-of-work recovery. Consider sleep hygiene support.
              </Banner>
            )}
          </div>
        </div>
      </Panel>

      <Panel title='ISFI — Intra-Shift Fatigue' sub={onShift(w) ? 'Currently on shift' : 'Last shift reading'}>
        <div className='flex flex-wrap gap-5 py-1'>
          <div className='flex-1'>
            <div className={`text-4xl font-bold ${toneText[fiTone]}`}>{fi}</div>
            <div className='text-[11px] uppercase tracking-wide text-muted-foreground'>ISFI Score</div>
          </div>
          <div className='flex flex-[2] flex-col justify-center gap-1.5'>
            <LabelBar label='EDA' value={w.edaScore} bar={w.edaScore} tone='info' />
            <LabelBar label='HR elev' value={`${hrElev}%`} bar={Math.min(hrElev, 100)} tone='warn' />
            <LabelBar label='Self-assess' value={`${assess}%`} bar={assess} tone='violet' />
          </div>
        </div>
      </Panel>
    </Grid>
  );
};
